import { PopulationHotText } from './../shared/interfaces/';

export class HotTextEvaluator {
  static isCorrect(
    population: PopulationHotText,
    selectedAnswersIndex: string[][]
  ): boolean {
    const { answers: correctAnswers } = population;
    correctAnswers[0] = correctAnswers[0].map(answer => answer.toLowerCase()); // convert all answers to lowercase before comparing
    const correctAnswersMap: { [name: string]: number } = {};
    const wordsSplit = population.wordsSplit as string[];
    const selectedAnswers: string[][] = [[]];
    selectedAnswersIndex[0].forEach(answer => {
      selectedAnswers[0].push(wordsSplit[parseInt(answer, 10)]);
    });
    // construct the correct Answers map where key is the word and value is the number of times it appears
    wordsSplit.forEach(word => {
      const trimmedWord = this._formatAnswerWord(word).toLowerCase(); // case insensitive check
      if (correctAnswers[0].includes(trimmedWord)) {
        if (trimmedWord in correctAnswersMap) {
          correctAnswersMap[trimmedWord] += 1;
        } else {
          correctAnswersMap[trimmedWord] = 1;
        }
      }
    });

    // selectedAnswersMap is the map where key is the word and value is the number of times the student has selected each word
    const selectedAnswersMap: { [name: string]: number } = {};
    selectedAnswers[0].forEach(word => {
      const trimmedWord = this._formatAnswerWord(word).toLowerCase(); // case insensitive check
      if (trimmedWord in selectedAnswersMap) {
        selectedAnswersMap[trimmedWord] += 1;
      } else {
        selectedAnswersMap[trimmedWord] = 1;
      }
    });

    // check if all the words in selectedAnswersMap are in the correctAnswersMap and exactly the same number of times
    for (const word in selectedAnswersMap) {
      if (word in correctAnswersMap) {
        if (selectedAnswersMap[word] !== correctAnswersMap[word]) return false;
        delete correctAnswersMap[word];
      } else return false;
    }
    if (Object.keys(correctAnswersMap).length > 0) return false;
    return true;
  }

  static getWrongAnswers(
    population: PopulationHotText,
    selectedAnswersIndex: string[][]
  ): string[][] {
    const wrong: string[][] = [[]];
    const correctAnswers = population.answers[0].map(answer =>
      answer.toLowerCase()
    ); // convert all answers to lowercase before comparing
    const selectedAnswers: string[][] = [[]];
    const wordsSplit = population.wordsSplit as string[];
    selectedAnswersIndex[0].forEach(answer => {
      selectedAnswers[0].push(wordsSplit[parseInt(answer, 10)]);
    });
    if (selectedAnswers && selectedAnswers[0]) {
      selectedAnswers[0].forEach((answer, index) => {
        const formattedAnswer = this._formatAnswerWord(answer).toLowerCase(); // case insensitive check
        if (!correctAnswers.includes(formattedAnswer)) {
          wrong[0].push(selectedAnswersIndex[0][index]);
        }
      });
    }
    return this.isEmptyAnswers(wrong)
      ? [['-1']] // return -1 when there are no wrong answers (and selectedAnswers has some value), but the student did not choose all occurrences of the right answer(s).
      : wrong;
  }

  static removeWrongAnswers(
    wrong: (string | number)[][],
    selectedAnswersIndex: string[][]
  ): string[][] {
    const result: string[][] = [[]];
    // remove all the indexes in wrong[0] from selectedAnswersIndex[0]
    selectedAnswersIndex[0].forEach(selectedIndex => {
      if (!wrong[0].includes(selectedIndex)) {
        result[0].push(selectedIndex);
      }
    });
    return result;
  }

  /**
   * Splits a paragraph into an array of words and phrases
   * Words are individual space-separated strings
   * Phrases are groups of words enclosed in square brackets
   * @param paragraph Input text containing words and [phrases in brackets]
   * @returns Array of words and phrases
   */
  static splitWordsAndPhrases(paragraph: string): void | string[] {
    // Handle empty or null input
    if (!paragraph?.trim()) {
      return;
    }

    const _addWordToData = (word: string): void => {
      if (word) {
        result.push(word);
      }
    };

    const result: string[] = [];
    let currentIndex = 0;
    const text = paragraph.trim();
    while (currentIndex < text.length) {
      if (text[currentIndex] === '[') {
        // Found start of a phrase
        const endBracketIndex = text.indexOf(']', currentIndex);
        if (endBracketIndex === -1) {
          // No closing bracket found, treat rest as regular text
          const remainingWords = text
            .slice(currentIndex)
            .split(/\s+/)
            .filter(Boolean);
          remainingWords.forEach(_addWordToData);
          break;
        }
        // Extract phrase without brackets
        const phrase = text.slice(currentIndex + 1, endBracketIndex);
        if (phrase.trim()) {
          _addWordToData(phrase.trim());
        }
        currentIndex = endBracketIndex + 1;
      } else {
        // Regular text - find next bracket or end
        const nextBracket = text.indexOf('[', currentIndex);
        const textToProcess =
          nextBracket === -1
            ? text.slice(currentIndex)
            : text.slice(currentIndex, nextBracket);

        // Split regular text into words
        const words = textToProcess.replace(/^[ ]+|[ ]+$/g, '').split(' ');
        words.forEach(word => {
          if (word.includes('\n')) {
            const splits = word.split('\n');
            for (let j = 0; j < splits.length; j++) {
              if (splits[j].length && splits[j]) {
                _addWordToData(splits[j]);
              }
              if (j < splits.length - 1) {
                result.push('\n');
              }
            }
          } else {
            _addWordToData(word);
          }
        });
        currentIndex = nextBracket === -1 ? text.length : nextBracket;
      }
    }
    return result;
  }

  static isEmptyAnswers(answers: string[][]): boolean {
    return !answers.length || !answers[0].length || !answers[0][0];
  }

  static _formatAnswerWord(answer: string): string {
    if (!answer || typeof answer !== 'string') return '';
    return answer.replace(/[^a-zA-Z0-9\s']/g, '').trim(); // format answer word to be compared with the correct answer
  }
}
